import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  DatePicker,
  Row,
  Col,
  Descriptions,
  Statistic
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { logAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const OperationLogList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState(null);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    loadStats();
  }, [page, pageSize, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await logAPI.getList({ page, pageSize, ...filters });
      if (res.data?.success) {
        setData(res.data.data.list);
        setTotal(res.data.data.total);
      }
    } catch (error) {
      message.error('加载日志列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await logAPI.getStats(filters);
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('加载统计失败:', error);
    }
  };

  const handleSearch = (values) => {
    const params = { ...values };
    if (values.dateRange) {
      params.startDate = values.dateRange[0].format('YYYY-MM-DD');
      params.endDate = values.dateRange[1].format('YYYY-MM-DD');
      delete params.dateRange;
    }
    setFilters(params);
    setPage(1);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setPage(1);
  };

  const handleViewDetail = async (record) => {
    try {
      const res = await logAPI.getDetail(record.id);
      if (res.data?.success) {
        setCurrentLog(res.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const getActionTag = (action) => {
    const colorMap = {
      view: 'blue',
      create: 'green',
      update: 'orange',
      delete: 'red',
      login: 'cyan',
      logout: 'purple'
    };
    const nameMap = {
      view: '查看',
      create: '创建',
      update: '更新',
      delete: '删除',
      login: '登录',
      logout: '登出'
    };
    return <Tag color={colorMap[action] || 'default'}>{nameMap[action] || action}</Tag>;
  };

  const getStatusTag = (status) => {
    return status === 'success'
      ? <Tag color="green">成功</Tag>
      : <Tag color="red">失败</Tag>;
  };

  const columns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '用户',
      key: 'user',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.user?.realName || record.username}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{record.username}</div>
        </div>
      )
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 100
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (action) => getActionTag(action)
    },
    {
      title: '操作描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '请求路径',
      dataIndex: 'requestUrl',
      key: 'requestUrl',
      width: 200,
      ellipsis: true,
      render: (url, record) => (
        <span>
          <Tag color="blue" style={{ marginRight: 4 }}>{record.requestMethod}</Tag>
          {url}
        </span>
      )
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 130
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      align: 'right',
      render: (val) => val ? `${val}ms` : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => getStatusTag(status)
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      )
    }
  ];

  // 计算统计数据
  const totalLogs = stats.statusStats?.reduce((sum, item) => sum + parseInt(item.count), 0) || 0;
  const successCount = stats.statusStats?.find(s => s.status === 'success')?.count || 0;
  const failureCount = stats.statusStats?.find(s => s.status === 'failure')?.count || 0;

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="总操作数" value={totalLogs} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="成功操作" value={successCount} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="失败操作" value={failureCount} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="keyword">
            <Input placeholder="用户名/描述/IP" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="module">
            <Select placeholder="模块" allowClear style={{ width: 120 }}>
              <Option value="认证">认证</Option>
              <Option value="商品管理">商品管理</Option>
              <Option value="客户管理">客户管理</Option>
              <Option value="供应商管理">供应商管理</Option>
              <Option value="销售订单">销售订单</Option>
              <Option value="采购订单">采购订单</Option>
              <Option value="库存管理">库存管理</Option>
              <Option value="财务管理">财务管理</Option>
              <Option value="系统管理">系统管理</Option>
            </Select>
          </Form.Item>
          <Form.Item name="action">
            <Select placeholder="操作类型" allowClear style={{ width: 100 }}>
              <Option value="view">查看</Option>
              <Option value="create">创建</Option>
              <Option value="update">更新</Option>
              <Option value="delete">删除</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" allowClear style={{ width: 80 }}>
              <Option value="success">成功</Option>
              <Option value="failure">失败</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card title="操作日志">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            }
          }}
        />
      </Card>

      {/* 日志详情弹窗 */}
      <Modal
        title="日志详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {currentLog && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="操作时间" span={2}>
              {dayjs(currentLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="操作用户">
              {currentLog.user?.realName || currentLog.username}
            </Descriptions.Item>
            <Descriptions.Item label="用户名">
              {currentLog.username}
            </Descriptions.Item>
            <Descriptions.Item label="操作模块">
              {currentLog.module}
            </Descriptions.Item>
            <Descriptions.Item label="操作类型">
              {getActionTag(currentLog.action)}
            </Descriptions.Item>
            <Descriptions.Item label="操作描述" span={2}>
              {currentLog.description}
            </Descriptions.Item>
            <Descriptions.Item label="请求方法">
              <Tag color="blue">{currentLog.requestMethod}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="请求路径">
              {currentLog.requestUrl}
            </Descriptions.Item>
            <Descriptions.Item label="IP地址">
              {currentLog.ip}
            </Descriptions.Item>
            <Descriptions.Item label="响应状态">
              {currentLog.responseStatus}
            </Descriptions.Item>
            <Descriptions.Item label="执行耗时">
              {currentLog.duration}ms
            </Descriptions.Item>
            <Descriptions.Item label="操作状态">
              {getStatusTag(currentLog.status)}
            </Descriptions.Item>
            {currentLog.requestParams && (
              <Descriptions.Item label="请求参数" span={2}>
                <pre style={{ maxHeight: 200, overflow: 'auto', margin: 0 }}>
                  {JSON.stringify(JSON.parse(currentLog.requestParams), null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {currentLog.errorMessage && (
              <Descriptions.Item label="错误信息" span={2}>
                <span style={{ color: '#cf1322' }}>{currentLog.errorMessage}</span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="用户代理" span={2}>
              <div style={{ wordBreak: 'break-all', fontSize: 12, color: '#666' }}>
                {currentLog.userAgent}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default OperationLogList;
